import {
    ArrowRight, List, Plus, Search, Sparkles, Trash2,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getProspectionLists, deleteProspectionList } from "../../interfaces/prospectionLists";
import { IProspectionListSummary } from "../../interfaces/prospectionList";
import { useProspectionListCounts } from "../../hooks/useProspectionListCounts";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Input } from "../../components/ui/Input";
import { ListBulletIcon } from "@heroicons/react/24/solid";
import { PageHeader } from "../../components/ui/PageHeader";
import { Skeleton } from "../../components/ui/Skeleton";

// ─── Skeleton ──────────────────────────────────────────────────────────────────

const NAME_W = ["w-48", "w-64", "w-40", "w-56", "w-44"];
const DESC_W = ["w-80", "w-96", "w-72", "", "w-64"];

/** The list cards while they load — same card, same lines, so nothing moves when they land. */
function ListsSkeleton() {
    return (
        <div className="space-y-2" aria-busy="true" aria-label="Chargement des listes">
            {NAME_W.map((w, i) => (
                <div key={i} className="rounded-2xl border border-border bg-surface p-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <Skeleton className={`h-4 ${w}`} />
                            <Skeleton className="h-5 w-20 rounded-full" />
                        </div>
                        {DESC_W[i] && <Skeleton className={`h-3 my-0.5 mb-1 max-w-full ${DESC_W[i]}`} />}
                        <Skeleton className="h-3 my-0.5 w-20" />
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <div className="min-w-[4rem] flex flex-col items-end">
                            <Skeleton className="h-6 w-12 my-0.5" />
                            <Skeleton className="h-3 w-10 my-0.5" />
                        </div>
                        <div className="p-2"><Skeleton className="h-4 w-4" /></div>
                        <Skeleton className="h-4 w-4" />
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function ProspectionLists() {
    const navigate = useNavigate();

    // Lists state
    const [lists, setLists] = useState<IProspectionListSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [deletingId, setDeletingId] = useState<string | null>(null);

    // Sizes arrive one request per list, after the rows are already painted — counting a
    // dynamic list is the expensive part, and none of it has to block the page.
    const counts = useProspectionListCounts(lists.map(l => l._id));

    useEffect(() => { fetchLists(); }, []);

    const fetchLists = async () => {
        try {
            setLoading(true);
            const data = await getProspectionLists();
            setLists(data);
        } catch (err) {
            console.error("Failed to fetch prospection lists:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!window.confirm("Supprimer cette liste ?")) return;
        setDeletingId(id);
        try {
            await deleteProspectionList(id);
            setLists(prev => prev.filter(l => l._id !== id));
        } catch (err) {
            console.error("Failed to delete list:", err);
        } finally {
            setDeletingId(null);
        }
    };

    const formatDate = (d: string) =>
        new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

    const filtered = lists.filter(
        l => l.name.toLowerCase().includes(search.toLowerCase()) ||
             (l.description || "").toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 pb-6 space-y-6">

                <PageHeader
                    icon={<ListBulletIcon />}
                    title="Mes listes"
                    subtitle="Gérez vos listes de leads et démarrez vos campagnes"
                    actions={
                        <Button variant="secondary" onClick={() => navigate("/prospection/discover")}>
                            <Sparkles className="h-4 w-4" />
                            Découvrir des listes
                        </Button>
                    }
                />

                {/* ── Search ── */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-faint" />
                    <Input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Rechercher une liste…"
                        className="pl-9"
                    />
                </div>

                {/* ── List ── */}
                {loading ? (
                    <ListsSkeleton />
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-44 rounded-2xl border border-border bg-surface">
                        <List className="h-10 w-10 text-ink-faint mb-3" />
                        <p className="text-sm font-semibold text-ink mb-1">
                            {lists.length === 0 ? "Aucune liste" : "Aucun résultat"}
                        </p>
                        <p className="text-xs text-ink-muted">
                            {lists.length === 0 ? "Créez votre première liste depuis la base ou découvrez des segments prêts à l'emploi" : "Essayez d'autres termes"}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filtered.map(list => {
                            const count = counts[list._id];
                            return (
                            <div
                                key={list._id}
                                onClick={() => navigate(`/prospection/${list._id}`)}
                                className="rounded-2xl border border-border bg-surface p-4 hover:bg-surface-sunken cursor-pointer transition-colors duration-150 flex items-center gap-4"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-semibold text-ink truncate">{list.name}</span>
                                        <Badge tone={list.type === "dynamic" ? "accent" : "neutral"}>
                                            {list.type === "dynamic" ? "Dynamique" : "Statique"}
                                        </Badge>
                                    </div>
                                    {list.description && <p className="text-xs text-ink-muted truncate mb-0.5">{list.description}</p>}
                                    <p className="text-xs text-ink-faint">{formatDate(list.createdAt)}</p>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <div className="text-right min-w-[4rem]">
                                        {count ? (
                                            <p className="text-lg font-semibold text-ink">
                                                {count.totalCount.toLocaleString("fr-FR")}{count.isCapped ? "+" : ""}
                                            </p>
                                        ) : (
                                            <div className="h-6 w-12 ml-auto rounded-md bg-surface-sunken motion-safe:animate-pulse" />
                                        )}
                                        <p className="text-xs text-ink-faint">
                                            leads{count?.isCapped ? " · max" : ""}
                                        </p>
                                    </div>
                                    <button
                                        onClick={e => handleDelete(list._id, e)}
                                        disabled={deletingId === list._id}
                                        className="p-2 rounded-lg text-ink-faint hover:text-danger hover:bg-danger-subtle transition-colors duration-150 disabled:opacity-40"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                    <ArrowRight className="h-4 w-4 text-ink-faint" />
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}

            </div>
        </div>
    );
}
